import { flattenDeep } from 'lodash';

interface Action {
  label: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface Permission {
  slug: string;
  granted: boolean;
  [key: string]: string | number | boolean | null | undefined;
}

interface Permissions {
  can_delete?: boolean;
  can_download?: boolean;
  can_edit?: boolean;
  can_share?: boolean;
  hires?: boolean;
  [key: string]: boolean | undefined;
}

interface AssetPerms {
  mediaPermissions: Permission[];
  userPerms: Permission[];
}

type ActionMap = Record<string, string[]>;

const actionMap: ActionMap = {
  can_delete: ['Delete', 'Delete Selected'],
  can_download: ['Download'],
  can_edit: ['Edit'],
  can_share: ['Share'],
};

const tryRemovePerm = (key: string, permissionAppliedActions: Action[], actionMap: ActionMap): Action[] => {
  const index = permissionAppliedActions.findIndex((item) => actionMap[key].includes(item.label));
  index > -1 && permissionAppliedActions.splice(index, 1);
  return permissionAppliedActions;
};

export const applyPermissions = (
  actions: Action[],
  permissions: Permissions,
  hiresActionValue: string | null,
): Action[] => {
  (actionMap as ActionMap).hires = hiresActionValue ? [hiresActionValue] : [];
  let permissionAppliedActions = actions;
  // loop keys of permissions object
  // if value not in array, remove it
  Object.keys(permissions).forEach((key) => {
    if (!permissions[key]) {
      permissionAppliedActions = tryRemovePerm(key, permissionAppliedActions, actionMap);
    }
  });
  if (!hiresActionValue) {
    permissionAppliedActions = tryRemovePerm('hires', permissionAppliedActions, actionMap);
  }
  return permissionAppliedActions;
};

type PermissionsInput = Permission[] | Record<string, boolean> | undefined;
type ActionsMap = ActionMap | Record<string, unknown[]>;

export const gatherActionsFromPerms = (perms: PermissionsInput, actionMap: ActionsMap): unknown[] => {
  if (!perms) return [];

  // Handle Record<string, boolean> format (userPerms from myPermissions)
  if (!Array.isArray(perms)) {
    const result: unknown[] = [];
    Object.entries(perms).forEach(([key, granted]) => {
      if (granted && actionMap[key]) {
        result.push(...(actionMap[key] as unknown[]));
      }
    });
    return flattenDeep(result);
  }

  // Handle Permission[] format
  const grantedPermissions = perms.filter((perm) => perm.granted);
  const final = grantedPermissions.map((perm) => actionMap[perm.slug]).filter((x) => x);
  return flattenDeep(final);
};

export const getPerm = (perms: Permission[], slug: string): Permission | undefined => {
  return perms.find((perm) => {
    return perm.slug === slug;
  });
};

export const gatherAssetPerms = (perms: AssetPerms, actionMap: ActionMap): string[] => {
  const combinedPerms = [...perms.mediaPermissions, ...perms.userPerms];
  return gatherActionsFromPerms(combinedPerms, actionMap) as string[];
};
